
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { format } from 'date-fns';
import { ClipboardCheck, Package } from 'lucide-react';

const QualityPendingList: React.FC = () => {
  const navigate = useNavigate();

  const { data: procurements, isLoading } = useQuery({
    queryKey: ['quality-pending-procurements'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('procurements')
        .select(`
          *,
          farms:farm_id(farmer_name, region),
          procurement_finance(payment_term_type),
          items:procurement_items(id, product_name, quantity)
        `)
        .eq('status', 'pending_quality')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    }
  });

  const getProductNames = (procurement: any) => {
    const items = (procurement as any).items || [];
    if (items.length === 0) {
      return procurement.product_name || 'Unknown Product';
    }
    if (items.length <= 3) {
      return items.map((i: any) => i.product_name).join(', ');
    }
    return `${items.slice(0, 2).map((i: any) => i.product_name).join(', ')} + ${items.length - 2} more`;
  };

  const handleInspect = (id: string) => {
    navigate(`/quality/inspect/${id}`);
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map(i => (
          <Skeleton key={i} className="h-32" />
        ))}
      </div>
    );
  }

  if (!procurements || procurements.length === 0) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <ClipboardCheck className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">No procurements pending quality inspection</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {procurements.map((procurement) => {
        const items = (procurement as any).items || [];
        return (
        <Card key={procurement.id} className="hover:shadow-md transition-shadow">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CardTitle className="text-lg">
                  {getProductNames(procurement)}
                </CardTitle>
                {items.length > 1 && (
                  <Badge variant="secondary" className="text-xs">
                    <Package className="h-3 w-3 mr-1" />
                    {items.length} items
                  </Badge>
                )}
              </div>
              <Badge variant="outline">{procurement.source_type}</Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div>
                <p className="text-sm text-muted-foreground">Quantity</p>
                <p className="font-medium">{procurement.total_quantity || 0} kg</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Source</p>
                <p className="font-medium">
                  {(procurement as any).farms?.farmer_name || procurement.source_type}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Created</p>
                <p className="font-medium">
                  {format(new Date(procurement.created_at), 'MMM dd, yyyy')}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Payment Type</p>
                <p className="font-medium">
                  {(procurement as any).procurement_finance?.payment_term_type || 'upfront'}
                </p>
              </div>
            </div>
            <Button onClick={() => handleInspect(procurement.id)} className="w-full sm:w-auto">
              <ClipboardCheck className="h-4 w-4 mr-2" />
              Start Quality Inspection
            </Button>
          </CardContent>
        </Card>
        );
      })}
    </div>
  );
};

export default QualityPendingList;
